import { useState, useCallback } from 'react';
import { formatMomentDate } from "./dateHelper";
import PreviewModal from '../components/modals/previewModal';
import { NoImgIcon, AxisBank, BankOfBaroda, BankOfIndia, CanaraBank, CentralBankOfIndia, HdfcBank, IciciBank, IdbiBank, IndianBank, KotakMahindra, NoBankIcon, PunjabNational, Sbi, Uco, UnionBankOfIndia } from '../assets/icons';


const isNil = (v) => v == null || v === "";

const goBack = (navigate, location, fallbackPath) => {
  const idx = window.history.state?.idx;
  if (typeof idx === 'number') {
    try {
      const historyStack = JSON.parse(sessionStorage.getItem("nav_history") || "[]");
      for (let i = idx - 1; i >= 0; i--) {
        const entry = historyStack[i];
        if (entry && entry.pathname !== location.pathname) {
          const delta = i - idx;
          navigate(delta);
          return;
        }
      }
    } catch (e) {
      console.error("Error during goBack navigation:", e);
    }
  }

  if (fallbackPath) {
    navigate(fallbackPath);
  } else {
    navigate(-1);
  }
};

const getPropertyMedia = (input) => {
  if (!input) return [];
  const mediaList = [];

  // If input is an array (e.g. row.photos or row.files or custom array of media)
  if (Array.isArray(input)) {
    input.forEach(item => {
      if (typeof item === 'string') {
        mediaList.push({
          type: 'image',
          url: item,
          title: 'Photo'
        });
      } else if (item && typeof item === 'object') {
        const url = item.url || item.file?.url;
        if (url) {
          const isVideo = url.match(/\.(mp4|webm|ogg|mov)$/i);
          mediaList.push({
            type: isVideo ? 'video' : 'image',
            url: url,
            title: isVideo ? 'Video' : 'Photo'
          });
        }
      }
    });
    return mediaList;
  }

  // If input is a row object
  if (Array.isArray(input.files)) {
    input.files.forEach(f => {
      const url = f.url || f.file?.url;
      if (url) {
        const isVideo = url.match(/\.(mp4|webm|ogg|mov)$/i);
        mediaList.push({
          type: isVideo ? 'video' : 'image',
          url: url,
          title: isVideo ? 'Video' : 'Photo'
        });
      }
    });
  }
  if (mediaList.length === 0 && Array.isArray(input.photos)) {
    input.photos.forEach(p => {
      const url = typeof p === 'string' ? p : p.url;
      if (url) {
        mediaList.push({
          type: 'image',
          url: url,
          title: 'Photo'
        });
      }
    });
  }
  if (input.video) {
    const url = typeof input.video === 'string' ? input.video : input.video.url;
    if (url) {
      mediaList.push({
        type: 'video',
        url: url,
        title: 'Video'
      });
    }
  }
  if (mediaList.length === 0) {
    const firstPhoto = input.files?.[0]?.url || input.files?.[0]?.file?.url || input.photos?.[0]?.url || "";
    if (firstPhoto) {
      mediaList.push({
        type: 'image',
        url: firstPhoto,
        title: 'Photo'
      });
    }
  }
  return mediaList;
};

const getPropertyFirstPhoto = (input) => {
  if (!input) return "";
  if (typeof input === 'string') return input;

  // If input is an array (e.g. photos or files array)
  if (Array.isArray(input)) {
    if (input.length === 0) return "";
    const first = input[0];
    if (typeof first === 'string') return first;
    return first?.url || first?.file?.url || "";
  }

  // If input is a row object
  return input.files?.[0]?.url || input.files?.[0]?.file?.url || input.photos?.[0]?.url || (typeof input.photos?.[0] === 'string' ? input.photos[0] : input.photos?.[0]?.url) || "";
};

const renderPropertyImageCell = (row, onPreview, getMediaFn = getPropertyMedia) => {
  const firstPhoto = getPropertyFirstPhoto(row);
  return firstPhoto ? (
    <img
      src={firstPhoto}
      alt="Property"
      className="rounded border"
      style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer" }}
      onError={(e) => { e.target.src = '/images/image-placeholder.png'; }}
      onClick={(e) => {
        e.stopPropagation();
        onPreview(getMediaFn(row));
      }}
    />
  ) : (
    <div
      className="bg-light rounded border d-flex align-items-center justify-content-center text-muted small"
      style={{ width: "50px", height: "50px", flexShrink: 0 }}
    >
      <NoImgIcon />
    </div>
  );
};

const usePropertyImagePreview = () => {
  const [previewMedia, setPreviewMedia] = useState(null);

  const renderPreviewModal = useCallback(() => (
    <PreviewModal
      show={!!previewMedia}
      onHide={() => setPreviewMedia(null)}
      previewMedia={previewMedia}
    />
  ), [previewMedia]);

  const renderCell = useCallback((valOrRow, rowOrGetMedia, getMediaFn = getPropertyMedia) => {
    let actualRow = valOrRow;
    let actualGetMedia = typeof rowOrGetMedia === 'function' ? rowOrGetMedia : getMediaFn;

    if (rowOrGetMedia && typeof rowOrGetMedia === 'object') {
      actualRow = rowOrGetMedia;
    }

    return renderPropertyImageCell(actualRow, setPreviewMedia, actualGetMedia);
  }, []);

  return {
    previewMedia,
    setPreviewMedia,
    renderPreviewModal,
    renderCell
  };
};
const getUserInitial = (name, length = 2) => {
  if (!name) return "UN";
  return name
    .trim()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase())
    .join("")
    .slice(0, length);
};
const UserInital = ({ name, width = 38, image = null, className = "", ...rest }) => {
  const { bg, color } = getUserInitialColors(name);
  const inlineStyle = {
    minWidth: `${width}px`,
    ...rest.style,
  };

  if (!image) {
    inlineStyle.backgroundColor = bg;
    inlineStyle.color = color;
  }

  return (
    <div
      className={`profile-icon overflow-hidden ${className}`}
      style={inlineStyle}
      {...rest}
    >
      {image ? (
        <img
          src={image}
          alt={name}
          className="object-fit-cover w-100 h-100"
        />
      ) : (
        getUserInitial(name)
      )}
    </div>
  );
};

const getUserInitialColors = (name) => {
  const palettes = [
    { bg: "#FDE5E9", color: "#9F2F48" }, // Pink/Red
    { bg: "#EEF8F1", color: "#34C759" }, // Green
    { bg: "#d6f4f8", color: "#00bad1" }, // Sky Blue
    { bg: "#fff0e1", color: "#ff9f43" }, // Orange
    { bg: "#edf7fe", color: "#0d3c5e" }, // Dark Blue
    { bg: "#e2e2e2", color: "#4b4b4b" }, // Gray
  ];

  if (!name) return palettes[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % palettes.length;
  return palettes[index];
};

const formatCurrencyIndianStyle = (value) => {
  if (!value) return '';
  // Convert to a string and split into integer and fractional parts
  const [integerPart, fractionalPart] = value.toString().split('.');
  // Format integer part with Indian numbering style
  const lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  const formattedInteger = otherNumbers
    ? otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree
    : lastThree;
  // Include fractional part only if it exists and is not "00"
  const formattedFractional = fractionalPart && fractionalPart !== '00' ? `.${fractionalPart}` : '';
  // Combine integer part with fractional part
  return `${formattedInteger}${formattedFractional}`;
};

// ✅ Format numbers into K, Lac, Cr with Indian system
const formatIndianCompact = (num) => {
  if (num === null || num === undefined) return "-";
  if (isNaN(num)) return num;

  if (num >= 1e7) {
    return (num / 1e7).toFixed(2).replace(/\.00$/, "") + " Cr";
  } else if (num >= 1e5) {
    return (num / 1e5).toFixed(2).replace(/\.00$/, "") + " Lac";
  } else if (num >= 1e3) {
    return (num / 1e3).toFixed(2).replace(/\.00$/, "") + " K";
  }
  return num.toString();
};

const formatDateFromString = (dateString, format = "DD-MM-YYYY") => {
  if (!dateString) return "-"; // Fallback for invalid input
  const dateTime = dateString.split(" at ")[1]; // Extract the date-time
  if (!dateTime) return "-"; // Fallback if no date-time part is found
  return formatMomentDate(dateTime, format);
};

const formatTimeFromString = (dateString, format = "hh:mm A") => {
  if (!dateString) return "-"; // Fallback for invalid input
  const dateTime = dateString.split(" at ")[1]; // Extract the date-time
  if (!dateTime) return "-"; // Fallback if no date-time part is found
  return formatMomentDate(dateTime, format);
};

// Transform response into { value, label } format
const mapToSelectOptions = (data = []) => {
  if (!Array.isArray(data)) return [];

  return data.map((item) => ({
    value: item.id,     // ✅ ALWAYS value
    label: item.name,   // ✅ ALWAYS string
  }));
};

const BankIcons = (value) => {
  switch (value) {
    case "State Bank of India (SBI)":
      return <Sbi />
    case "Punjab National Bank (PNB)":
      return <PunjabNational />
    case "Bank of Baroda (BoB)":
      return <BankOfBaroda />
    case "Canara Bank":
      return <CanaraBank />
    case "Union Bank of India":
      return <UnionBankOfIndia />
    case "Indian Bank":
      return <IndianBank />
    case "Bank of India (BOI)":
      return <BankOfIndia />
    case "Central Bank of India":
      return <CentralBankOfIndia />
    case "UCO Bank":
      return <Uco />
    case "IDBI Bank":
      return <IdbiBank />
    case "HDFC Bank":
      return <HdfcBank />
    case "ICICI Bank":
      return <IciciBank />
    case "Axis Bank":
      return <AxisBank />
    case "Kotak Mahindra Bank":
      return <KotakMahindra />
    default:
      return <NoBankIcon />; // Fallback icon URL
  }
};
export {
  BankIcons,
  mapToSelectOptions,
  formatTimeFromString,
  formatDateFromString,
  formatIndianCompact,
  formatCurrencyIndianStyle,
  getUserInitialColors,
  getUserInitial,
  usePropertyImagePreview,
  renderPropertyImageCell,
  getPropertyFirstPhoto,
  getPropertyMedia,
  UserInital,
  isNil,
  goBack
};